import { useState, useEffect } from 'react';
import { Flame, Leaf, Plus, ChevronDown, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import PickupDiscountBanner from '@/components/PickupDiscountBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Todos', 
  'Aperitivos', 
  'Sopas',
  'Platos Vegetal',
  'Plato Principal',
  'Plato Especial',
  'Plato Al Horno Indu',
  'Plato Biryani',
  'Arroz',
  'Pan',
  'Menu Infantil',
  'Postres', 
  'Bebidas'
];

const SPICINESS_OPTIONS = [
  { value: 'sin_picante', label: 'Sin Picante' },
  { value: 'poco_picante', label: 'Poco Picante' },
  { value: 'medio_picante', label: 'Medio Picante' },
  { value: 'muy_picante', label: 'Muy Picante' },
];

// Items that default to Poco Picante with only Medio/Muy options
const POCO_PICANTE_DEFAULT = [
  'Chilli Chicken', 'Garlic Chicken',
  'Rogan Josh (Cordero)', 'Rogan Josh (Gamba)', 'Rogan Josh (Pollo)', 'Rogan Josh (Verdura)',
];
const POCO_PICANTE_OPTIONS = [
  { value: 'poco_picante', label: 'Poco Picante' },
  { value: 'medio_picante', label: 'Medio Picante' },
  { value: 'muy_picante', label: 'Muy Picante' },
];

// Items that default to Muy Picante with only Medio option
const MUY_PICANTE_DEFAULT = [
  'Vindaloo (Cordero)', 'Vindaloo (Gamba)', 'Vindaloo (Pollo)', 'Vindaloo (Verdura)',
  'Phall (Cordero)', 'Phall (Gamba)', 'Phall (Pollo)', 'Phall (Verdura)',
  'Madras (Cordero)', 'Madras (Gamba)', 'Madras (Pollo)', 'Madras (Verdura)',
];
const MUY_PICANTE_OPTIONS = [
  { value: 'medio_picante', label: 'Medio Picante' },
  { value: 'muy_picante', label: 'Muy Picante' },
];

const getSpicyConfig = (item) => {
  if (POCO_PICANTE_DEFAULT.includes(item.name)) {
    return { options: POCO_PICANTE_OPTIONS, defaultValue: 'poco_picante' };
  }
  if (MUY_PICANTE_DEFAULT.includes(item.name)) {
    return { options: MUY_PICANTE_OPTIONS, defaultValue: 'muy_picante' };
  }
  return { options: SPICINESS_OPTIONS, defaultValue: 'sin_picante' };
};

const NO_SPICINESS_CATEGORIES = ['Aperitivos', 'Postres', 'Bebidas', 'Menu Infantil', 'Pan', 'Arroz'];
const NO_SPICINESS_NAMES = ['Murg Mango', 'Butter Chicken', 'Kashmiri Gosht', 'Chicken Mughlai', 'Chicken Pasanda'];

const SIDE_DISH_OPTIONS = [
  { value: 'patata', label: 'Con Patata' },
  { value: 'arroz', label: 'Con Arroz' },
];

const SAUCE_OPTIONS = [
  { value: 'masala', label: 'Masala' },
  { value: 'korma', label: 'Korma' },
  { value: 'curry', label: 'Curry' },
];

const KIDS_SAUCE_NAME = 'Menu Infantil\nPollo con salsa';
const hasSideDish = (item) => item.category === 'Menu Infantil' && item.name !== KIDS_SAUCE_NAME;

const hasSauceChoice = (item) => item.name === KIDS_SAUCE_NAME;

const hasSpiciness = (item) => {
  if (NO_SPICINESS_CATEGORIES.includes(item.category)) return false;
  if (NO_SPICINESS_NAMES.includes(item.name)) return false;
  return true;
};

const MenuSection = ({ menuItems }) => {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const { addToCart, isLoading } = useCart();
  const [settings, setSettings] = useState({ online_ordering_enabled: true, whatsapp_ordering_enabled: true });
  const [spicinessSelections, setSpicinessSelections] = useState({});
  const [sideDishSelections, setSideDishSelections] = useState({});
  const [sauceSelections, setSauceSelections] = useState({});
  // Allergen matrix: { allergens: [{key,label,icon}], matrix: { item_id: [keys] } }
  const [allergenData, setAllergenData] = useState({ allergens: [], matrix: {} });
  const [expandedDesc, setExpandedDesc] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/allergens`).then(r => setAllergenData({
      allergens: r.data.allergens || [],
      matrix: r.data.matrix || {},
    })).catch(() => {});
  }, []);

  const allergenInfo = (key) => allergenData.allergens.find(a => a.key === key) || { key, label: key, icon: '?' };

  const orderingEnabled = settings.online_ordering_enabled || settings.whatsapp_ordering_enabled;

  // Normalize for accent-insensitive matching (José → jose, etc.)
  const normalize = (s) => (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const query = normalize(searchQuery.trim());

  // Search overrides category filter — if the user is searching, show matches
  // from all categories. When the search is cleared, category filter applies.
  const filteredItems = query
    ? menuItems.filter((item) => {
        const haystack = normalize(`${item.name} ${item.description || ''} ${item.category || ''}`);
        return haystack.includes(query);
      })
    : activeCategory === 'Todos'
      ? menuItems
      : menuItems.filter((item) => item.category === activeCategory);

  const handleSpicinessChange = (itemId, value) => {
    setSpicinessSelections(prev => ({ ...prev, [itemId]: value }));
  };

  const handleSideDishChange = (itemId, value) => {
    setSideDishSelections(prev => ({ ...prev, [itemId]: value }));
  };

  const handleSauceChange = (itemId, value) => {
    setSauceSelections(prev => ({ ...prev, [itemId]: value }));
  };

  const handleAddToCart = async (item) => {
    const needsSpiciness = hasSpiciness(item);
    const needsSideDish = hasSideDish(item);
    const needsSauce = hasSauceChoice(item);
    
    let label = null;
    if (needsSpiciness) {
      const config = getSpicyConfig(item);
      const spiciness = spicinessSelections[item.id] || config.defaultValue;
      label = config.options.find(o => o.value === spiciness)?.label;
    } else if (needsSideDish) {
      const sideDish = sideDishSelections[item.id] || 'patata';
      label = SIDE_DISH_OPTIONS.find(o => o.value === sideDish)?.label;
    } else if (needsSauce) {
      const sauce = sauceSelections[item.id] || 'masala';
      label = SAUCE_OPTIONS.find(o => o.value === sauce)?.label;
    }
    
    await addToCart(item.id, 1, label);
    toast.success(`${item.name} anadido al carrito`, {
      description: label ? `${item.price.toFixed(2)} EUR - ${label}` : `${item.price.toFixed(2)} EUR`,
    });
  };

  return (
    <section 
      id="menu" 
      className="section-padding bg-[#FDFBF7]"
      data-testid="menu-section"
    >
      <div className="container-max">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="overline mb-4 block">Nuestros Platos</span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#2D231E] mb-4">
            Explora Nuestro Menu
          </h2>
          <p className="font-sans text-base md:text-lg text-[#5C4D43] max-w-2xl mx-auto">
            Cada plato es una obra maestra culinaria, preparado con amor y las mejores especias de la India.
          </p>
        </div>

        {/* Pickup discount nudge */}
        <PickupDiscountBanner />

        {/* Search bar */}
        <div className="max-w-xl mx-auto mb-6">
          <div className="relative">
            <Search className="w-5 h-5 text-[#5C4D43] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar plato, por ejemplo 'pollo tikka', 'biryani'..."
              className="pl-12 pr-12 py-6 text-base bg-white border-[#E8DEC8] focus:ring-[#C44536] focus:border-transparent rounded-full shadow-sm"
              data-testid="menu-search-input"
              aria-label="Buscar en el menu"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-[#5C4D43] hover:text-[#C44536] rounded-full"
                aria-label="Limpiar busqueda"
                data-testid="menu-search-clear"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          {query && (
            <p className="mt-2 text-center text-sm text-[#5C4D43] font-sans" data-testid="menu-search-count">
              {filteredItems.length === 0
                ? 'Ningun plato coincide con tu busqueda.'
                : `${filteredItems.length} plato${filteredItems.length === 1 ? '' : 's'} encontrado${filteredItems.length === 1 ? '' : 's'}`}
            </p>
          )}
          <p className="mt-3 text-center text-xs text-[#5C4D43] font-sans">
            Tienes alergias? Consulta la{' '}
            <a
              href="/alergenos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C44536] underline font-semibold inline-flex items-center gap-1"
              data-testid="menu-allergens-link"
            >
              tabla completa de alergenos
            </a>{' '}
            antes de pedir.
          </p>
        </div>

        {/* Category Tabs — hidden while searching so results span all categories */}
        {!query && (
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`category-tab ${
                  activeCategory === category ? 'category-tab-active' : 'category-tab-inactive'
                }`}
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              className="menu-card overflow-hidden animate-fade-in-up opacity-0"
              style={{ 
                animationDelay: `${Math.min(index * 30, 300)}ms`,
                animationFillMode: 'forwards'
              }}
              data-testid={`menu-item-${item.id}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden bg-[#E8DEC8]">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center p-4">
                      <span className="font-serif text-4xl text-[#C44536]">&#127835;</span>
                      <p className="font-sans text-xs text-[#5C4D43] mt-2">{item.category}</p>
                    </div>
                  </div>
                )}
                <div className="absolute top-3 left-3 flex gap-2">
                  {item.is_vegetarian && (
                    <span className="veg-indicator bg-[#2C5E3E]/10 px-2 py-1 rounded-full">
                      <Leaf className="w-3 h-3" />
                      Veg
                    </span>
                  )}
                  {item.is_spicy && (
                    <span className="spicy-indicator bg-[#C44536]/10 px-2 py-1 rounded-full">
                      <Flame className="w-3 h-3" />
                      Picante
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-serif text-lg font-semibold text-[#2D231E] leading-tight whitespace-pre-line">
                    {item.name}
                  </h3>
                  <span className="font-sans text-lg font-bold text-[#C44536] whitespace-nowrap ml-2">
                    {item.price.toFixed(2)} EUR
                  </span>
                </div>
                {item.is_available === false && (
                  <div className="mb-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium inline-block">No disponible</div>
                )}
                <p
                  className={`font-sans text-sm text-[#5C4D43] mb-1 ${expandedDesc[item.id] ? '' : 'line-clamp-3'}`}
                  data-testid={`item-desc-${item.id}`}
                >
                  {item.description}
                </p>
                {item.description && item.description.length > 90 && (
                  <button
                    type="button"
                    onClick={() => setExpandedDesc(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                    className="text-xs text-[#C44536] hover:text-[#A03629] font-medium font-sans mb-3 underline-offset-2 hover:underline"
                    data-testid={`toggle-desc-${item.id}`}
                  >
                    {expandedDesc[item.id] ? 'Ver menos' : 'Ver mas'}
                  </button>
                )}
                {!(item.description && item.description.length > 90) && <div className="mb-2" />}

                {/* Allergen badges */}
                {(allergenData.matrix[item.id] || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 mb-3" data-testid={`allergens-${item.id}`}>
                    <span className="font-sans text-[10px] uppercase tracking-wide text-[#8A7A6D] mr-0.5">Contiene:</span>
                    {(allergenData.matrix[item.id] || []).map((key) => {
                      const info = allergenInfo(key);
                      return (
                        <span
                          key={key}
                          title={info.label}
                          aria-label={`Contiene ${info.label}`}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FDFBF7] border border-[#E8DEC8] text-base cursor-help hover:border-[#C44536] transition-colors"
                        >
                          <span aria-hidden="true">{info.icon}</span>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Spiciness Dropdown */}
                {hasSpiciness(item) && (() => {
                  const config = getSpicyConfig(item);
                  return (
                  <div className="mb-3">
                    <Select
                      value={spicinessSelections[item.id] || config.defaultValue}
                      onValueChange={(val) => handleSpicinessChange(item.id, val)}
                    >
                      <SelectTrigger 
                        className="w-full bg-white border-[#E8DEC8] text-sm h-9"
                        data-testid={`spiciness-select-${item.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Flame className="w-3.5 h-3.5 text-[#C44536]" />
                          <SelectValue placeholder="Nivel de picante" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {config.options.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} data-testid={`spiciness-${opt.value}-${item.id}`}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  );
                })()}

                {/* Side Dish Dropdown (Menu Infantil except Pollo con salsa) */}
                {hasSideDish(item) && (
                  <div className="mb-3">
                    <Select
                      value={sideDishSelections[item.id] || 'patata'}
                      onValueChange={(val) => handleSideDishChange(item.id, val)}
                    >
                      <SelectTrigger 
                        className="w-full bg-white border-[#E8DEC8] text-sm h-9"
                        data-testid={`sidedish-select-${item.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <SelectValue placeholder="Acompanamiento" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {SIDE_DISH_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} data-testid={`sidedish-${opt.value}-${item.id}`}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Sauce Dropdown (Pollo con salsa only) */}
                {hasSauceChoice(item) && (
                  <div className="mb-3">
                    <Select
                      value={sauceSelections[item.id] || 'masala'}
                      onValueChange={(val) => handleSauceChange(item.id, val)}
                    >
                      <SelectTrigger 
                        className="w-full bg-white border-[#E8DEC8] text-sm h-9"
                        data-testid={`sauce-select-${item.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <SelectValue placeholder="Tipo de salsa" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {SAUCE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} data-testid={`sauce-${opt.value}-${item.id}`}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  onClick={() => handleAddToCart(item)}
                  disabled={isLoading || !orderingEnabled || item.is_available === false}
                  className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
                  data-testid={`add-to-cart-${item.id}`}
                >
                  <Plus className="w-4 h-4" />
                  {item.is_available === false ? 'No disponible' : orderingEnabled ? 'Anadir al Carrito' : 'Pedidos no disponibles'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MenuSection;
